// Types
import EventEmitter from "@folio-2026/shared/eventemitter"
import * as THREE from "three"
import { HDRLoader } from "three/addons/loaders/HDRLoader.js"
import { KTX2Loader } from "three/addons/loaders/KTX2Loader.js"
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js"
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js"
import { Font, FontLoader } from "three/examples/jsm/loaders/FontLoader.js"
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js"
import type { WebGPURenderer } from "three/webgpu"

export type Source = {
  name: string
  path: string | string[]
  type: string
}

export type Sources = Source[]

export type LoadResult = (
  | THREE.Texture
  | THREE.CubeTexture
  | THREE.DataTexture
  | Font
  | GLTF
  | string
  | ArrayBuffer
) & {
  cameras?: THREE.PerspectiveCamera[]
  animations?: THREE.AnimationClip[]
  scene?: THREE.Scene
  wrapS?: number
  wrapT?: number
  minFilter?: number
}

/**
 * Type helper to extract GLTF-only resources from a resources object
 */
export type GLTFResources<T extends { [key: string]: LoadResult }> = {
  [K in keyof T]: T[K] extends GLTF ? T[K] : never
}

/**
 * Type predicate to check if a LoadResult is a GLTF
 */
function isGLTF(value: LoadResult): value is GLTF & LoadResult {
  return (
    typeof value === "object" &&
    value !== null &&
    "scenes" in value &&
    "asset" in value &&
    "parser" in value
  )
}

/**
 * Extract GLTF-only resources from a resources items object
 * @param items - The resources items object
 * @returns A typed object containing only GLTF resources
 */
export function getGLTFResources(items: { [key: string]: LoadResult }): {
  [key: string]: GLTF
} {
  const gltfResources: { [key: string]: GLTF } = {}
  for (const [key, value] of Object.entries(items)) {
    if (isGLTF(value)) {
      gltfResources[key] = value as GLTF
    }
  }
  return gltfResources
}

export default class Resources extends EventEmitter {
  items: { [key: string]: LoadResult }
  sources: Sources
  loaders: {
    dracoLoader?: DRACOLoader
    gltfLoader?: GLTFLoader
    textureLoader?: THREE.TextureLoader
    fileLoader?: THREE.FileLoader
    rgbeLoader?: HDRLoader
    ktx2Loader?: KTX2Loader
    fontLoader?: FontLoader
  }
  manager: THREE.LoadingManager
  ktx2Initialized: boolean = false
  glbSources: Sources = []
  glbLoadCount: number = 0
  glbTotalCount: number = 0
  readyTriggered: boolean = false

  constructor(sources: Sources) {
    super()

    // https://threejs.org/docs/#api/en/loaders/FileLoader
    THREE.Cache.enabled = true

    // Options
    this.sources = sources

    this.items = {}

    this.manager = new THREE.LoadingManager(
      this.onResourceLoad.bind(this),
      this.onResourceProgress.bind(this),
    )

    this.loaders = {}

    const dracoLoader = new DRACOLoader()
    dracoLoader.setDecoderPath(
      "/draco/",
      // dracoDecoderPath.replace(/draco_decoder\.js$/, ""),
    )

    // KTX2Loader needs Basis transcoder files
    const ktx2Loader = new KTX2Loader(this.manager).setTranscoderPath(
      "/basis/",
      // basisTranscoderPath.replace(/basis_transcoder\.js$/, ""),
    )

    this.loaders.ktx2Loader = ktx2Loader

    this.loaders.gltfLoader = new GLTFLoader(this.manager)
      .setKTX2Loader(ktx2Loader)
      .setMeshoptDecoder(MeshoptDecoder)
      .setDRACOLoader(dracoLoader)
      .setCrossOrigin("anonymous")

    this.loaders.rgbeLoader = new HDRLoader(this.manager)

    this.loaders.fontLoader = new FontLoader(this.manager)

    this.loaders = {
      ...this.loaders,
      textureLoader: new THREE.TextureLoader(this.manager),
      fileLoader: new THREE.FileLoader(this.manager),
    }

    // Separate GLB sources from other sources
    this.glbSources = this.sources.filter(
      (source) => source.type === "gltfModel",
    )
    this.glbTotalCount = this.glbSources.length

    // Start loading non-GLB resources immediately
    // GLB files will be loaded after KTX2Loader is initialized
    this.startLoading(true)
  }

  /**
   * Initialize KTX2Loader with renderer support detection
   * This must be called after the renderer is created
   * Based on: https://github.com/mrdoob/three.js/blob/master/examples/webgl_loader_gltf_compressed.html
   * For WebGPU: await renderer.init() first to ensure backend is ready (r181+)
   */
  async setKTX2Renderer(renderer: THREE.WebGLRenderer | WebGPURenderer) {
    if (this.loaders.ktx2Loader && !this.ktx2Initialized) {
      try {
        // Ensure renderer is ready before calling detectSupport
        if (!renderer) {
          throw new Error("Renderer is not available")
        }

        // For WebGPU, wait one frame to ensure the renderer context is fully ready
        // The renderer.init() has already been called in Experience.init(),
        // so we just need to wait for the context to be ready
        // This is necessary for detectSupport() to work correctly
        if ("init" in renderer && typeof renderer.init === "function") {
          await new Promise((resolve) => requestAnimationFrame(resolve))
        }

        // Use synchronous detectSupport for both WebGL and WebGPU
        // For WebGPU, detectSupport needs the renderer to be fully initialized
        // and the context to be ready
        this.loaders.ktx2Loader.detectSupport(renderer as THREE.WebGLRenderer)
        this.ktx2Initialized = true
        this.startLoadingGLB()
      } catch (error) {
        console.error("KTX2Loader: Failed to detect support", error)
        // Even if detection fails, try loading anyway
        this.ktx2Initialized = true
        this.startLoadingGLB()
      }
    }
  }

  /**
   * This is called by LoadingManager when non-GLB resources finish loading
   * We need to check if we should trigger ready or wait for GLB files
   */
  onResourceLoad() {
    this.checkAndTriggerReady()
  }

  /**
   * Check if all resources are loaded and trigger ready event
   */
  checkAndTriggerReady() {
    // Prevent multiple triggers
    if (this.readyTriggered) {
      return
    }

    const hasGLBFiles = this.glbTotalCount > 0
    const allGLBLoaded = this.glbLoadCount >= this.glbTotalCount

    // Trigger ready if:
    // 1. No GLB files exist, OR
    // 2. KTX2Loader is initialized AND all GLB files are loaded
    const shouldTrigger = !hasGLBFiles || (this.ktx2Initialized && allGLBLoaded)

    if (shouldTrigger) {
      // Only trigger if GLB files are actually loaded (or don't exist)
      if (hasGLBFiles && this.glbLoadCount === 0) {
        return
      }

      this.readyTriggered = true
      this.trigger("ready")
    }
  }

  onResourceProgress(url: string, loaded: number, total: number) {
    this.trigger("progress", [url, loaded, total])
  }

  /**
   * Get a typed subset of resources containing only GLTF models
   * This method filters the items and returns only GLTF entries
   */
  getGLTFResources(): { [key: string]: GLTF } {
    return getGLTFResources(this.items)
  }

  startLoading(skipGLB = false) {
    if (this.sources.length === 0) {
      requestAnimationFrame(() => {
        this.onResourceLoad()
      })
    }

    for (const source of this.sources) {
      switch (source.type) {
        case "gltfModel":
          // Skip GLB loading if KTX2Loader isn't initialized yet
          if (skipGLB) {
            continue
          }
          // Note: createImageBitmap is needed for KTX2 textures
          // Don't disable it when using KTX2-compressed textures
          // https://discourse.threejs.org/t/error-with-the-gltfloader-unable-to-load-texture-blob/32465/5
          // window.createImageBitmap = undefined

          this.loaders.gltfLoader?.load(
            source.path as string,
            (file) => {
              this.items[source.name] = file as LoadResult
            },
            () => null,
            (error) => {
              console.error("Error loading resource:", error)
            },
          )
          break

        case "texture":
          this.loaders.textureLoader?.load(source.path as string, (file) => {
            this.items[source.name] = file
            // The LoadingManager's onLoad might not fire if KTX2Loader is loading transcoder files
            // Check if all non-GLB resources are loaded
            const nonGLBSources = this.sources.filter(
              (s) => s.type !== "gltfModel",
            )
            const loadedNonGLB = nonGLBSources.filter(
              (s) => s.name in this.items,
            )

            // If all non-GLB resources are loaded, manually trigger the check
            // The LoadingManager's onLoad might not fire if KTX2Loader is loading transcoder files
            if (loadedNonGLB.length === nonGLBSources.length) {
              this.checkAndTriggerReady()
            }
          })
          break

        case "file": {
          this.loaders.fileLoader?.load(source.path as string, (file) => {
            this.items[source.name] = file
          })
          break
        }

        case "rgbe": {
          this.loaders.rgbeLoader?.load(source.path as string, (file) => {
            this.items[source.name] = file
          })
          break
        }

        case "font": {
          this.loaders.fontLoader?.load(source.path as string, (file) => {
            this.items[source.name] = file
          })
          break
        }

        default:
          break
      }
    }
  }

  /**
   * Start loading GLB files after KTX2Loader is initialized
   */
  startLoadingGLB() {
    if (this.glbSources.length === 0) {
      // No GLB files to load, trigger ready if not already triggered
      this.checkAndTriggerReady()
      return
    }

    for (const source of this.glbSources) {
      this.loaders.gltfLoader?.load(
        source.path as string,
        (file) => {
          this.items[source.name] = file as LoadResult
          this.glbLoadCount++
          // Check if all GLB files are loaded
          if (this.glbLoadCount >= this.glbTotalCount) {
            this.checkAndTriggerReady()
          }
        },
        () => null,
        (error) => {
          console.error("Error loading GLB:", source.name, error)
          this.glbLoadCount++
          // Still count failed loads to prevent infinite waiting
          if (this.glbLoadCount >= this.glbTotalCount) {
            this.checkAndTriggerReady()
          }
        },
      )
    }
  }
}
